import { Fragment } from "react";
import { Popover, Transition } from "@headlessui/react";
import Link from "next/link";

import {
  StorefrontIcon,
  TruckIcon,
  CheckCircleIcon,
  CarIcon,
  ListIcon,
  XIcon,
  CaretDownIcon,
  MagnifyingGlassIcon,
} from "@phosphor-icons/react";
import MenuLink from "./menu-link";
import { useRouter } from "next/router";

import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import clsx from "clsx";

const navigation = [
  { name: "Storefront", href: "/" },
  { name: "Catalogue", href: "/catalogue" },
  { name: "Fulfilment", href: "/fulfilment" },
  { name: "Pricing", href: "/pricing" },
];

const solutions = [
  {
    name: "MMY Vehicle Search",
    description:
      "Add a vehicle search to your parts store to help customers quickly find the right part for their car.",
    href: "https://mmyvs.partbot.io",
    icon: MagnifyingGlassIcon,
  },
  {
    name: "Registration Plate Search",
    description:
      "Help your customers find the right part for their vehicle using our PL8SRCH rego lookup.",
    href: "/number-plate-search",
    icon: CarIcon,
  },
  {
    name: "Parts Cataloguing (PIM)",
    description:
      "Link your automotive parts to vehicles, add detailed fitment info and media with our fast, intuitive parts cataloguing platform.",
    href: "/brands",
    icon: CheckCircleIcon,
  },
  {
    name: "Shipping Rates",
    description:
      "Provide your customers with live, accurate shipping rates at checkout.",
    href: "/shipping-rates",
    prefetch: false,
    new: false,
    icon: TruckIcon,
  },
];

export default function Header() {
  const router = useRouter();

  return (
    <header className="fixed top-0 z-20 w-full px-4 pt-4 md:px-0">
      <Popover className="relative">
        {({ open }) => (
          <div className="container mx-auto">
            <div className="flex items-center justify-between rounded-xl border border-emerald-300/10 bg-stone-950/80 px-2 py-2 backdrop-blur-md md:mx-auto md:justify-start md:space-x-10 md:py-2">
              <div className="flex justify-start lg:w-0 lg:flex-1">
                <a
                  href="/"
                  className="flex items-center justify-start pl-3 pr-2.5"
                >
                  <img
                    className="h-[20px] w-auto"
                    src="/partbot-wordmark-light.svg"
                    alt="Partbot"
                  />
                </a>
              </div>
              <div className="md:hidden">
                <Popover.Button as={Button} variant="outline" size="icon">
                  <span className="sr-only">Open menu</span>
                  <ListIcon className="size-4" aria-hidden="true" />
                </Popover.Button>
              </div>
              <Popover.Group as="nav" className="hidden space-x-10 md:flex">
                <Popover className="relative">
                  {({ open }) => (
                    <>
                      <Popover.Button
                        as={Button}
                        size="sm"
                        variant="ghost"
                        className=""
                      >
                        <span>Products</span>
                        <CaretDownIcon
                          className={clsx(
                            "h-3 w-3 transition duration-300 ease-out",
                            open && "rotate-180",
                          )}
                        />
                      </Popover.Button>

                      <Transition
                        show={open}
                        as={Fragment}
                        enter="transition ease-out duration-200"
                        enterFrom="opacity-0 translate-y-1"
                        enterTo="opacity-100 translate-y-0"
                        leave="transition ease-in duration-150"
                        leaveFrom="opacity-100 translate-y-0"
                        leaveTo="opacity-0 translate-y-1"
                      >
                        <Popover.Panel
                          static
                          className="absolute z-10 -ml-4 mt-5 w-screen max-w-md transform lg:left-1/2 lg:ml-0 lg:max-w-2xl lg:-translate-x-1/2"
                        >
                          <div className="overflow-hidden rounded-xl border border-emerald-100/10">
                            <div className="relative grid gap-6 bg-stone-950 px-5 py-6 sm:gap-8 sm:p-8 lg:grid-cols-2">
                              {solutions.map((item) => (
                                <Popover.Button
                                  as={MenuLink}
                                  href={item.href}
                                  prefetch={item.prefetch}
                                  key={item.name}
                                  className="-m-3 flex items-start rounded-lg px-5 py-3 transition hover:bg-emerald-700/10"
                                >
                                  <div className="flex aspect-square size-12 shrink-0 items-center justify-center rounded-lg border border-emerald-300/10 bg-linear-to-br from-emerald-950/80 to-emerald-950/10">
                                    <item.icon
                                      className="size-6 text-emerald-500"
                                      aria-hidden="true"
                                    />
                                  </div>
                                  <div className="ml-4">
                                    <div className="flex items-center gap-2 text-sm font-bold leading-5 text-stone-100">
                                      {item.name}
                                      {item.new && (
                                        <Badge variant="secondary">New</Badge>
                                      )}
                                    </div>
                                    <div className="mt-1 text-xs text-stone-400">
                                      {item.description}
                                    </div>
                                  </div>
                                </Popover.Button>
                              ))}
                            </div>
                          </div>
                        </Popover.Panel>
                      </Transition>
                    </>
                  )}
                </Popover>

                {navigation.map((item) => (
                  <Button
                    key={item.name}
                    variant={
                      router.pathname === item.href ? "secondary" : "ghost"
                    }
                    size="sm"
                    asChild
                  >
                    <Link href={item.href}>{item.name}</Link>
                  </Button>
                ))}
              </Popover.Group>
              <div className="hidden items-center justify-end gap-2 md:flex md:flex-1 lg:w-0">
                <Button variant="secondary" size="sm" asChild>
                  <Link href="https://partbot.io">Log in</Link>
                </Button>
                <Button size="sm" className="bg-emerald-500" asChild>
                  <Link href="/contact">Contact Us</Link>
                </Button>
              </div>
            </div>

            <Transition
              show={open}
              as={Fragment}
              enter="duration-200 ease-out"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="duration-100 ease-in"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Popover.Panel
                focus
                static
                className="absolute inset-x-0 top-0 z-30 origin-top-right transform transition md:hidden"
              >
                <div className="relative w-full divide-y divide-dashed divide-emerald-100/30 rounded-xl border border-dotted border-emerald-400/60 bg-stone-950/90 px-8 py-4 text-foreground backdrop-blur-lg [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground [&>svg~*]:pl-7">
                  <div className="pb-6 pt-3">
                    <div className="absolute right-4 top-4">
                      <Popover.Button as={Button} variant="outline" size="icon">
                        <span className="sr-only">Close menu</span>
                        <XIcon />
                      </Popover.Button>
                    </div>
                    <div className="mt-6">
                      <nav className="grid grid-cols-1 gap-6">
                        {solutions.map((item) => (
                          <Popover.Button
                            as={MenuLink}
                            href={item.href}
                            prefetch={item.prefetch}
                            key={item.name}
                            className="-my-3 flex w-full grow items-center rounded-lg p-2 hover:bg-stone-900"
                          >
                            <div className="flex aspect-square size-10 shrink-0 items-center justify-center rounded-lg border border-emerald-300/10 bg-linear-to-br from-emerald-950/80 to-emerald-950/10">
                              {item.fontawesome ? (
                                <item.icon
                                  className="size-4 text-lg text-emerald-500"
                                  aria-hidden="true"
                                />
                              ) : (
                                <item.icon
                                  className="size-4"
                                  aria-hidden="true"
                                />
                              )}
                            </div>
                            <div className="ml-4 flex items-center gap-2 text-sm font-medium text-stone-100">
                              {item.name}
                              {item.new && (
                                <Badge variant="secondary">New</Badge>
                              )}
                            </div>
                          </Popover.Button>
                        ))}
                      </nav>
                    </div>
                  </div>
                  <div className="py-3">
                    <div className="flex flex-col items-start justify-start gap-2">
                      {navigation.map((item) => (
                        <Button
                          key={item.name}
                          variant="ghost"
                          className="w-full items-start justify-start text-left"
                          asChild
                        >
                          <Link href={item.href}>{item.name}</Link>
                        </Button>
                      ))}
                    </div>
                    <div className="mt-6 flex gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        className="w-full"
                        asChild
                      >
                        <Link href="https://partbot.io">Log in</Link>
                      </Button>
                      <Button size="sm" asChild className="w-full">
                        <Link href="/contact">Contact Us</Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </Popover.Panel>
            </Transition>
          </div>
        )}
      </Popover>
    </header>
  );
}
